import React, { useEffect, useState } from "react";
import { useGetApplicationQuery, usePostApplicantMutation, usePostUpsertApplicationMutation } from "@/store/service";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ApplicationStep } from "@/enum/ApplicationStep";
import ApplicationApplicant from "./ApplicationApplicant";
import Applicants from "@/model/interface/form/Applicants";
import { isDebugForms, getDebugNextStepUrl } from "@/util/debug";
import { applicantsPersonalSchema } from "@/model/schemas";
import { zodFormResolver } from "@/util/form";
import Skeleton from "react-loading-skeleton";

const ApplicationApplicantWrapper = () => {
  const navigate = useNavigate();
  const params = useParams();
  const applicationId = params.id ?? "";
  const applicantType = params.type;
  const isValidType = applicantType === "main-applicant" || applicantType === "co-applicant";

  const { data: application, isLoading } = useGetApplicationQuery(applicationId, {
    skip: !applicationId || !isValidType,
  });
  const [postApplication, postApplicationResult] = usePostUpsertApplicationMutation();
  const [postApplicant, postApplicantResult] = usePostApplicantMutation();

  const [hasCoApplicant, setCoApplicant] = useState(
    !!application?.applicants?.find((applicant) => applicant.type === "CoApplicant"),
  );

  const form = useForm<Applicants>({
    resolver: zodFormResolver<Applicants>(applicantsPersonalSchema),
    mode: "onTouched",
    defaultValues: {},
  });

  const { control, watch, register, formState, handleSubmit, getValues } = form;

  useEffect(() => {
    if (!applicationId || !isValidType) {
      navigate("/");
    }
  }, [applicationId, isValidType]);

  useEffect(() => {
    if (postApplicantResult.isSuccess && isValidType) {
      const step = applicantType === "main-applicant" ? ApplicationStep.Applicant : ApplicationStep.CoApplicant;
      postApplication({ id: applicationId, step });
    }
  }, [postApplicantResult]);

  if (!applicationId || !isValidType) {
    return null;
  }

  if (isLoading) {
    return <Skeleton count={5} height="2.75em" className="mt-4" />;
  }

  const onSubmit = async (data: Applicants) => {
    data.mainApplicant.type = "MainApplicant";
    if (data.coApplicant) {
      data.coApplicant.type = "CoApplicant";
    }

    if (applicantType === "main-applicant") {
      postApplicant({ data: data.mainApplicant, applicationId });
    }

    if (data.coApplicant && applicantType === "co-applicant") {
      postApplicant({ data: data.coApplicant, applicationId });
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDebugForms()) {
      navigate(getDebugNextStepUrl(applicationId, ApplicationStep.Applicant, applicantType));
    } else {
      handleSubmit(onSubmit)(e);
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="relative">
      <div className="w-full max-w-2xl mx-auto space-y-6">
        <h3 className="text-2xl font-semibold text-gray-900 text-center">Applicant details</h3>
        <ApplicationApplicant form={form} type={applicantType} />
        <div className="flex items-start gap-3">
          <input
            className="mt-1 size-5 rounded border-gray-300 text-[#0FD131] focus:ring-[#9BFBE0]"
            id="co-applicant"
            type="checkbox"
            checked={hasCoApplicant}
            onChange={(e) => setCoApplicant(e.target.checked)}
          />
          <label htmlFor="co-applicant" className="text-sm text-gray-700">
            Check this box if there is a second person applying for the loan (spouse, partner, etc.)
          </label>
        </div>
        {hasCoApplicant && (
          <>
            <h3 className="text-2xl font-semibold text-gray-900 text-center">Co-Applicant details</h3>
            <ApplicationApplicant form={form} type="co-applicant" />
          </>
        )}
        <div className="flex justify-center pt-2">
          <button
            type="submit"
            name="submit"
            className="inline-flex items-center rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
          >
            Continue
          </button>
        </div>
      </div>
    </form>
  );
};

export default ApplicationApplicantWrapper;
